import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, X, ArrowLeft } from "lucide-react";
import { getOperation, getLevel, getFractionSubOp, getDecimalSubOp, getAlgebraSubOp, getGeometriaSubOp, QUESTIONS_PER_ROUND } from "../lib/constants";
import { generateRound } from "../lib/mathGen";
import { recordRound, loadProfile } from "../lib/storage";
import { computeUnlockedMedals } from "../lib/medals";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { FractionVisual, parseFraction } from "../components/FractionVisual";
import { GeometryVisual } from "../components/GeometryVisual";
import { startRound, recordGuestRound, FREE_DAILY_LIMIT, guestRoundsToday } from "../lib/billing";
import { recordRankingRound } from "../lib/ranking";
import { useEntitlement } from "../contexts/EntitlementContext";

export default function Practice({ profile, onUpdate }) {
  const { operationId, subOp, levelId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { entitlement, refresh: refreshEntitlement } = useEntitlement();

  const isAlgMode = location.pathname.startsWith("/secundaria/algebra");
  const isGeomMode = location.pathname.startsWith("/secundaria/geometria");
  const isSecundariaMode = isAlgMode || isGeomMode;
  const isDecMode = !isSecundariaMode && location.pathname.startsWith("/practica/decimales");
  const isFracMode = !isSecundariaMode && !isDecMode && !!subOp;

  // Gate: check entitlement before allowing the round to begin
  const [gateChecked, setGateChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Secundaria is premium-only
      if (isSecundariaMode && entitlement && !entitlement.entitled) {
        navigate("/upgrade", { replace: true, state: { reason: "secundaria_locked" } });
        return;
      }
      try {
        const res = await startRound();
        if (cancelled) return;
        if (res.guest) {
          // Local rate limit for guests
          const rt = guestRoundsToday();
          if (rt >= FREE_DAILY_LIMIT) {
            navigate("/upgrade", { replace: true, state: { reason: "daily_limit" } });
            return;
          }
          recordGuestRound();
        } else if (!res.allowed) {
          navigate("/upgrade", { replace: true, state: { reason: "daily_limit" } });
          return;
        }
        setGateChecked(true);
        if (refreshEntitlement) refreshEntitlement();
      } catch (e) {
        // On error, fall through and let user practice (defensive)
        setGateChecked(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveOpId = isAlgMode
    ? "algebra"
    : isGeomMode
    ? "geometria"
    : isFracMode
    ? "fracciones"
    : isDecMode
    ? "decimales"
    : operationId;
  const level = getLevel(levelId);
  const sub = isAlgMode
    ? getAlgebraSubOp(subOp)
    : isGeomMode
    ? getGeometriaSubOp(subOp)
    : isFracMode
    ? getFractionSubOp(subOp)
    : isDecMode
    ? getDecimalSubOp(subOp)
    : null;
  // For secundaria, build a synthetic "op" using the sub-op styling
  const op = isAlgMode
    ? {
        id: "algebra",
        name: "Álgebra",
        color: sub?.color || "#7B61FF",
        colorLight: sub?.colorLight || "#E9E3FF",
        borderDark: sub?.borderDark || "#3D2EA0",
      }
    : isGeomMode
    ? {
        id: "geometria",
        name: "Geometría",
        color: sub?.color || "#10B981",
        colorLight: sub?.colorLight || "#D1FAE5",
        borderDark: sub?.borderDark || "#065F46",
      }
    : getOperation(effectiveOpId);

  const problems = useMemo(
    () => generateRound(effectiveOpId, levelId, QUESTIONS_PER_ROUND, (isFracMode || isDecMode || isAlgMode || isGeomMode) ? subOp : undefined),
    [effectiveOpId, levelId, subOp, isFracMode, isDecMode, isAlgMode, isGeomMode]
  );

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [streak, setStreak] = useState(0);
  const [finished, setFinished] = useState(false);

  // Use refs for round tallies to avoid stale-state in finishRound
  const correctRef = useRef(0);
  const wrongRef = useRef(0);
  const pointsRef = useRef(0);
  const maxStreakRef = useRef(0);

  // Re-renderable display values
  const [correctCount, setCorrectCount] = useState(0);
  const [points, setPoints] = useState(0);

  useEffect(() => {
    if (!op || !level) navigate("/dashboard");
  }, [op, level, navigate]);

  if (!op || !level) return null;
  if (!gateChecked) {
    return (
      <PageShell>
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="font-heading text-xl text-[#5C5C60]">Preparando ronda…</div>
        </div>
      </PageShell>
    );
  }

  const current = problems[index];
  const progress = ((index + (feedback ? 1 : 0)) / QUESTIONS_PER_ROUND) * 100;

  const handleAnswer = (val) => {
    if (feedback) return;
    setSelected(val);
    if (val === current.answer) {
      setFeedback("correct");
      const newStreak = streak + 1;
      setStreak(newStreak);
      maxStreakRef.current = Math.max(maxStreakRef.current, newStreak);
      correctRef.current += 1;
      pointsRef.current += level.points;
      setCorrectCount(correctRef.current);
      setPoints(pointsRef.current);
    } else {
      setFeedback("wrong");
      setStreak(0);
      wrongRef.current += 1;
    }
  };

  const goNext = () => {
    if (index + 1 >= QUESTIONS_PER_ROUND) {
      finishRound();
      return;
    }
    setIndex((i) => i + 1);
    setSelected(null);
    setFeedback(null);
  };

  const finishRound = () => {
    if (finished) return;
    setFinished(true);
    const correct = correctRef.current;
    const wrong = wrongRef.current;
    const earned = pointsRef.current;
    const ms = maxStreakRef.current;
    const lastRound = {
      operationId: effectiveOpId,
      levelId,
      correct,
      wrong,
      maxStreak: ms,
      points: earned,
      questions: QUESTIONS_PER_ROUND,
    };
    const updated = recordRound(loadProfile() || profile, {
      operationId: effectiveOpId,
      correct,
      wrong,
      pointsEarned: earned,
      medalsUnlocked: [],
    });
    const unlocked = computeUnlockedMedals(updated, lastRound);
    if (unlocked.length > 0) {
      const withMedals = {
        ...updated,
        medals: Array.from(new Set([...(updated.medals || []), ...unlocked])),
      };
      localStorage.setItem("mateaventura.profile", JSON.stringify(withMedals));
    }
    if (onUpdate) onUpdate();
    // Record points on the leaderboard (fire-and-forget)
    if (earned > 0) recordRankingRound(earned).catch(() => {});
    navigate("/resultados", {
      state: {
        lastRound: { ...lastRound, unlockedMedals: unlocked },
        operationId: effectiveOpId,
        levelId,
        subOp: (isFracMode || isDecMode || isAlgMode || isGeomMode) ? subOp : undefined,
        family: isAlgMode ? "algebra" : isGeomMode ? "geometria" : isFracMode ? "fracciones" : isDecMode ? "decimales" : undefined,
      },
      replace: true,
    });
  };

  return (
    <PageShell>
      {/* Top bar with progress */}
      <div className="flex items-center gap-3 sm:gap-4">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center justify-center w-11 h-11 rounded-full bg-white border-2 border-gray-200 hover:bg-gray-50"
          data-testid="practice-exit-btn"
          aria-label="Salir"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
        </button>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-[#5C5C60]">
              Pregunta {index + 1} de {QUESTIONS_PER_ROUND}
            </span>
            <span className="text-xs sm:text-sm font-bold text-[#7A5C00]" data-testid="practice-points">
              ⭐ {points} pts
            </span>
          </div>
          <div className="h-3 rounded-full bg-white border-2 border-white shadow-inner overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ backgroundColor: op.color }}
              animate={{ width: `${progress}%` }}
              transition={{ type: "spring", bounce: 0.2 }}
              data-testid="practice-progress"
            />
          </div>
        </div>
      </div>

      {/* Streak indicator */}
      {streak >= 2 && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mt-2 sm:mt-4 inline-flex items-center gap-2 bg-[#FFF6CC] border-2 border-[#FFD700] rounded-full px-3 py-1 sm:px-4 sm:py-1.5"
          data-testid="practice-streak"
        >
          <span className="text-sm sm:text-lg">🔥</span>
          <span className="font-bold text-xs sm:text-sm text-[#7A5C00]">Racha: {streak}</span>
        </motion.div>
      )}

      {/* Question card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.95 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="mt-3 sm:mt-8"
        >
          <div
            className={`bg-white rounded-3xl sm:rounded-[2.5rem] border-4 sm:border-8 border-white shadow-[0_15px_30px_-15px_rgba(0,0,0,0.15)] p-4 sm:p-10 text-center ${
              feedback === "wrong" ? "shake" : ""
            }`}
            style={{ borderTopColor: op.color, borderTopWidth: "8px" }}
          >
            <p className="text-xs sm:text-sm font-bold uppercase tracking-wider mb-2 sm:mb-4" style={{ color: op.color }}>
              {isAlgMode
                ? `Álgebra · ${sub.short}`
                : isGeomMode
                ? `Geometría · ${sub.short}`
                : isFracMode
                ? `Fracciones · ${sub.short}`
                : isDecMode
                ? `Decimales · ${sub.short}`
                : op.name} · {level.name}
            </p>
            {isFracMode && (
              <div
                className="inline-flex items-center gap-1.5 sm:gap-2 bg-[#FFF6CC] border-2 border-[#FFD700] rounded-full px-2.5 py-0.5 sm:px-3 sm:py-1 mb-2 sm:mb-3"
                data-testid="frac-simplify-hint"
              >
                <span className="text-sm">💡</span>
                <span className="text-[10px] sm:text-sm font-bold text-[#7A5C00]">
                  Simplifica tu respuesta a su mínima expresión
                </span>
              </div>
            )}
            <motion.div
              key={`q-${index}`}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", bounce: 0.5, delay: 0.1 }}
              className={`font-heading font-bold text-[#1C1C1E] tracking-tight ${
                isGeomMode
                  ? "text-lg sm:text-2xl lg:text-3xl leading-snug"
                  : "text-4xl sm:text-6xl lg:text-7xl"
              }`}
              data-testid="practice-question"
            >
              {isAlgMode && subOp === "ecuaciones" ? (
                <>
                  {current.question}
                  <div className="mt-2 sm:mt-3 text-2xl sm:text-3xl text-[#5C5C60] font-bold">
                    x = ?
                  </div>
                </>
              ) : isGeomMode ? (
                <>{current.question}</>
              ) : (
                <>
                  {current.question} <span className="text-[#5C5C60]">= ?</span>
                </>
              )}
            </motion.div>

            {/* Geometry visual */}
            {isGeomMode && current.geomVisual && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-4 sm:mt-6 flex items-center justify-center"
                data-testid="practice-geom-visual"
              >
                <GeometryVisual
                  visual={current.geomVisual}
                  color={op.color}
                  unit={current.unit || "cm"}
                />
              </motion.div>
            )}

            {/* Fraction visuals */}
            {current.visuals && current.visuals.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="mt-3 sm:mt-6 flex items-center justify-center gap-2 sm:gap-5 flex-wrap"
                data-testid="practice-visuals"
              >
                {current.visuals.map((v, vi) => (
                  <React.Fragment key={vi}>
                    <div className="flex flex-col items-center gap-0.5">
                      <FractionVisual
                        numerator={v.numerator}
                        denominator={v.denominator}
                        size={48}
                        color={op.color}
                        strokeColor={op.borderDark}
                        emptyColor={op.colorLight}
                      />
                      <span className="text-[10px] sm:text-xs font-bold text-[#5C5C60]">
                        {v.numerator}/{v.denominator}
                      </span>
                    </div>
                    {vi < current.visuals.length - 1 && (
                      <span
                        className="font-heading text-2xl sm:text-3xl font-bold"
                        style={{ color: op.color }}
                      >
                        {current.operator || "+"}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </motion.div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Choices */}
      <div className="mt-3 sm:mt-6 grid grid-cols-2 gap-2 sm:gap-4" data-testid="practice-choices">
        {current.choices.map((choice) => {
          const isSelected = selected === choice;
          const isCorrectChoice = feedback && choice === current.answer;
          const isWrongSelected = feedback === "wrong" && isSelected;

          let bg = "white";
          let border = "#E5E7EB";
          let textCol = "#1C1C1E";

          if (isCorrectChoice) {
            bg = "#E8F9EE";
            border = "#34C759";
            textCol = "#1A6B2F";
          } else if (isWrongSelected) {
            bg = "#FFEAEA";
            border = "#FF6B6B";
            textCol = "#803535";
          } else if (isSelected) {
            border = op.color;
          }

          return (
            <motion.button
              key={choice}
              onClick={() => handleAnswer(choice)}
              disabled={!!feedback}
              whileTap={{ scale: 0.96 }}
              whileHover={!feedback ? { scale: 1.02, y: -2 } : {}}
              className="clay-btn relative rounded-2xl border-4 border-b-[6px] sm:border-b-[8px] px-4 py-3 sm:px-6 sm:py-6 font-heading text-2xl sm:text-4xl font-bold disabled:cursor-not-allowed"
              style={{ backgroundColor: bg, borderColor: border, color: textCol }}
              data-testid={`choice-${choice}`}
            >
              {(() => {
                const frac = parseFraction(choice);
                if (frac && isFracMode) {
                  return (
                    <span className="inline-flex items-center gap-2 sm:gap-3">
                      <FractionVisual
                        numerator={frac.numerator}
                        denominator={frac.denominator}
                        size={36}
                        color={op.color}
                        strokeColor={op.borderDark}
                        emptyColor={op.colorLight}
                      />
                      <span>{choice}</span>
                    </span>
                  );
                }
                if (isGeomMode && current.unit) {
                  return (
                    <span className="inline-flex items-baseline gap-1">
                      <span>{choice}</span>
                      <span className="text-sm sm:text-lg font-bold text-[#5C5C60]">
                        {current.unit}
                      </span>
                    </span>
                  );
                }
                return choice;
              })()}
              {isCorrectChoice && (
                <Check
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 sm:w-7 sm:h-7 text-[#34C759]"
                  strokeWidth={4}
                />
              )}
              {isWrongSelected && (
                <X
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 w-5 h-5 sm:w-7 sm:h-7 text-[#FF6B6B]"
                  strokeWidth={4}
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Feedback / Next */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3 sm:mt-6 flex flex-row items-center justify-between gap-2 sm:gap-4 bg-white rounded-2xl border-4 border-white shadow-md p-3 sm:p-5"
            data-testid="practice-feedback"
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {feedback === "correct" ? (
                <>
                  <span className="text-2xl sm:text-3xl shrink-0">🎉</span>
                  <div className="min-w-0">
                    <p className="font-heading text-base sm:text-xl font-bold text-[#1A6B2F]">
                      ¡Muy bien!
                    </p>
                    <p className="text-xs sm:text-sm text-[#5C5C60]">+{level.points} puntos</p>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-2xl sm:text-3xl shrink-0">💪</span>
                  <div className="min-w-0">
                    <p className="font-heading text-sm sm:text-xl font-bold text-[#803535] leading-tight">
                      Era {current.answer}
                    </p>
                    <p className="text-xs sm:text-sm text-[#5C5C60]">¡Tú puedes!</p>
                  </div>
                </>
              )}
            </div>
            <ClayButton
              onClick={goNext}
              color={op.color}
              borderColor={op.borderDark}
              size="sm"
              className="shrink-0"
              data-testid="practice-next-btn"
            >
              {index + 1 >= QUESTIONS_PER_ROUND ? "Resultados" : "Siguiente"}
            </ClayButton>
          </motion.div>
        )}
      </AnimatePresence>
    </PageShell>
  );
}
